import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Using JavaScript to commit to Github – CodeWithSwiz 7",
  "description": "Update code with a JavaScript function? Easier than you think 😍",
  "published": "2020-09-17T00:00:00.000Z",
  "categories": "CodeWithSwiz, Technical, NextJS, Livecoding",
  "hero": "./img/screenshot-1600360451663.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Update code with a JavaScript function? Easier than you think 😍`}</p>
    <p><em parentName="p">{`CodeWithSwiz is a twice-a-week live show. Like a podcast with video and fun hacking. Focused on experiments. `}<a parentName="em" {...{
          "href": "https://youtube.com/swizecteller"
        }}>{`Join live Wednesdays and Sundays`}</a></em></p>
    <lite-youtube {...{
      "videoid": "Ql_Ni24Aj4Y",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`Episode 7 continues where `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/why-nextjs-api-routes-are-fab-codewithswiz-6/"
      }}>{`we left off`}</a>{` – got the server /api routes, need to write to GitHub.`}</p>
    <p>{`You can't do this in the browser, you'd leak API tokens. Easy mistake to ignore, but GitHub tokens are serious.`}</p>
    <p>{`GitHub tokens are keys to your kingdom. An attacker could take this and overwrite any and all code you own.`}</p>
    <p>{`Leaking important tokens is serious enough that `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/what-happens-when-you-push-aws-credentials-to-github/"
      }}>{`AWS blocked my whole account when I shared a token`}</a>{`.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/l04DWtTzSqI8b0bhwv/giphy-loop.mp4?cid=7cf28b0ffoqaivzk0xuibac52iluxe80b36yddcr9nf6755a&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "why-write-to-github"
    }}>{`Why write to GitHub`}</h2>
    <p>{`We're exploring NextJS by building a headless CMS. Modern static site generators use markdown files to represent each article.`}</p>
    <p>{`Add a dash of Netlify or Vercel and you get a wonderful workflow: Write markdown, commit to git, push to GitHub, trigger deploy.`}</p>
    <p>{`And that's what our `}<inlineCode parentName="p">{`/api`}</inlineCode>{` method needs to do.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/api/publishArticle.js

export default async (req, res) => {
  const status = await commitArticle(req.body);

  res.status(status).json({ success: true });
};
`}</code></pre>
    <p>{`Think of the API method as your controller. Gets a request, does work, returns the result. In this case the status code we get from GitHub.`}</p>
    <h2 {...{
      "id": "how-to-use-javascript-to-commit-to-github"
    }}>{`How to use JavaScript to commit to GitHub`}</h2>
    <p>{`GitHub maintains an open source `}<a parentName="p" {...{
        "href": "https://github.com/octokit/rest.js/"
      }}><inlineCode parentName="a">{`@octokit/rest`}</inlineCode>{` library`}</a>{`. Works great, but I find the documentation difficult to untangle.`}</p>
    <p>{`Here's what you do:`}</p>
    <ol>
      <li parentName="ol">{`auth with your GitHub token`}</li>
      <li parentName="ol">{`prep data to commit`}</li>
      <li parentName="ol">{`check for an existing file SHA hash`}</li>
      <li parentName="ol">{`commit your code`}</li>
    </ol>
    <h3 {...{
      "id": "1-auth-with-your-github-token"
    }}>{`1. auth with your GitHub token`}</h3>
    <p>{`We're using `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/github/authenticating-to-github/creating-a-personal-access-token"
      }}>{`Github's personal access token`}</a>{` stored in a `}<inlineCode parentName="p">{`.env.local`}</inlineCode>{` file for now. We'll add an authentication flow later.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/api/publishArticle.js
import { Octokit } from "@octokit/rest";

const octokit = new Octokit({
  auth: process.env.GITHUB_TOKEN,
});
`}</code></pre>
    <p>{`This gives you an authenticated octokit library to use. Hardcoded to your user.`}</p>
    <h3 {...{
      "id": "2-prep-data-to-commit"
    }}>{`2. prep data to commit`}</h3>
    <p>{`GitHub requires that you send files encoded as Base64.`}</p>
    <p>{`We use the `}<a parentName="p" {...{
        "href": "https://www.npmjs.com/package/js-base64"
      }}>{`js-base64`}</a>{` library for that. Dealing with native Node buffers and encoding/decoding felt like too much.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/api/publishArticle.js

    content: Base64.encode(
        \`\${frontmatter(article)}\\n\\n\${article.markdown}\`
    ),
`}</code></pre>
    <p>{`Calls the `}<inlineCode parentName="p">{`frontmatter`}</inlineCode>{` method on our article, smashes it together with the `}<inlineCode parentName="p">{`markdown`}</inlineCode>{` content, and encodes them both into Base64.`}</p>
    <p>{`You get a string that's safe to send over the internets without losing character encodings or binary info.`}</p>
    <p>{`PS: we share the `}<inlineCode parentName="p">{`frontmatter`}</inlineCode>{` method between server and browser via a `}<inlineCode parentName="p">{`utils`}</inlineCode>{` library. Both ends are JavaScript and that makes code sharing super convenient ✌️`}</p>
    <h3 {...{
      "id": "3-check-for-an-existing-file-sha-hash"
    }}>{`3. check for an existing file SHA hash`}</h3>
    <p>{`GitHub freaks out when you commit over a file that exists. You need to say you're doing this on purpose.`}</p>
    <p>{`For that, you'll need the current SHA blob of the file you're overwriting. Thanks to Aleš for finding the right method to use on chat during the stream.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/api/publishArticle.js

async function getSHA(path) {
  const result = await octokit.repos.getContent({
    owner: "Swizec",
    repo: "test-repo",
    path,
  });

  const sha = result?.data?.sha;

  return sha;
}
`}</code></pre>
    <p>{`Call that method with the path to your file and it returns the correct SHA string to use. Among other info like the contents of the file, the commit it belongs to, etc.`}</p>
    <h3 {...{
      "id": "4-commit-your-code"
    }}>{`4. commit your code`}</h3>
    <p>{`The final "publish an article as a single Markdown file" method looks like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// pages/api/publishArticle.js

async function commitArticle(article) {
  const path = \`\${slug(article.title)}.mdx\`;
  const sha = await getSHA(path);

  const result = await octokit.repos.createOrUpdateFileContents({
    owner: "Swizec",
    repo: "test-repo",
    path,
    message: \`Add article "\${article.title}"\`,
    content: Base64.encode(\`\${frontmatter(article)}\\n\\n\${article.markdown}\`),
    sha,
  });

  return result?.status || 500;
}
`}</code></pre>
    <p>{`Create our filename from the title, check for any existing SHA, create or update the file.`}</p>
    <p>{`You get `}<a parentName="p" {...{
        "href": "https://github.com/Swizec/test-repo"
      }}>{`a test repository`}</a>{` with 2 files and a couple commits`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "890px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/1854908f27eb18f69b2df4e64c5c9b51/6c86f/Commit-change-for-a-test-article005e75.png",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "69.05829596412556%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'276\\'%20viewBox=\\'0%200%20400%20276\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M47%2018l-2%201-10%201-1%202c0%202%201%202%203%202%203%200%204%200%204-2l1-2%201%202c1%202%202%202%209%202%209%200%209%200%209-3%200-2%200-2-2-2-2%201-3%200-3-1l-1-1-1%201c0%202-5%202-6%200h-1m70%200l-1%201c-11%200-11%200-11%203s6%203%207%200l1-2%201%202c1%202%205%203%205%201h1l6%201c6%200%206%200%206-2s-1-2-3-2l-2-2-1-1-1%201c0%201-1%202-2%201l-3%201c-1%201-1%201-1-1l-1-2-1%201m60%200l-6%201-5%201h-4l-1-1v2c0%203%205%204%206%202h1c0%202%207%201%208-1%200-2%200-2%201%200%200%202%201%202%204%202s3%200%203-3c0-4%200-5-3-2h-2c0-2-2-3-2-1M60%2076v1l-2%201-2%201c0%202%205%202%205%200h1l4%202%203-2h1c0%202%205%204%206%202h4c4%200%206-1%205-5h-2l-8%201-7%201h-1l-1-1-2%201-2-1-2-1m68%200l-4%201c-2%200-3%201-3%202%200%203%2024%203%2025%200h1c0%202%204%203%205%201%201-1-1-4-2-3h-9c-1%201-1%201-1-1h-5c0-2-2-1-4%201-1%201-1%201-2-1h-1m49%200l-2%203c0%203%2014%202%2014%200h1c0%203%2010%203%2010%200%201-1%201-1%201%201h2l3%201c2%200%202%200%201-1l-1-2-7-1-6-1v-1l-1%201h-2l-4%201h-1c0-2%200-2-1-1l-2%201h-3c0-2-1-2-2-1m215%2029v86l1%2076v-86a4254%204254%200%2000-1-76M75%20156c-1%203%200%206%201%204h4l3-1h2c2%202%205%203%207%201s2-2%203-1l7%201c6%200%206%200%205-2%200-2-11-2-11-1H78l-3-1m31%20113\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1854908f27eb18f69b2df4e64c5c9b51/ca0a1/Commit-change-for-a-test-article005e75.webp 223w", "/static/1854908f27eb18f69b2df4e64c5c9b51/75680/Commit-change-for-a-test-article005e75.webp 445w", "/static/1854908f27eb18f69b2df4e64c5c9b51/8d1ba/Commit-change-for-a-test-article005e75.webp 890w", "/static/1854908f27eb18f69b2df4e64c5c9b51/3838e/Commit-change-for-a-test-article005e75.webp 1335w", "/static/1854908f27eb18f69b2df4e64c5c9b51/9d85b/Commit-change-for-a-test-article005e75.webp 1720w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/1854908f27eb18f69b2df4e64c5c9b51/e92b6/Commit-change-for-a-test-article005e75.png 223w", "/static/1854908f27eb18f69b2df4e64c5c9b51/e66bf/Commit-change-for-a-test-article005e75.png 445w", "/static/1854908f27eb18f69b2df4e64c5c9b51/4ef49/Commit-change-for-a-test-article005e75.png 890w", "/static/1854908f27eb18f69b2df4e64c5c9b51/4e814/Commit-change-for-a-test-article005e75.png 1335w", "/static/1854908f27eb18f69b2df4e64c5c9b51/6c86f/Commit-change-for-a-test-article005e75.png 1720w"],
                "sizes": "(max-width: 890px) 100vw, 890px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/1854908f27eb18f69b2df4e64c5c9b51/4ef49/Commit-change-for-a-test-article005e75.png",
                "alt": "Commit change for a test article",
                "title": "Commit change for a test article",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Commit change for a test article`}</figcaption>{`
  `}</figure></p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/xUPJPm5MGaotqMnv56/giphy-loop.mp4?cid=7cf28b0fyrj2aq0zh5sbbm6pnl945p6zwvq5de2sibo81pag&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Cheers,
~Swizec`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      